import React from 'react';
import { User, CircleCheck as CheckCircle, Clock, CreditCard } from 'lucide-react';
import type { ReferralTreeNode } from '../services/ReferralService';

interface ReferralTreeProps {
  nodes: ReferralTreeNode[];
  currentUserName?: string;
}

const DEPTH_COLORS: Record<number, { bg: string; border: string; text: string; line: string }> = {
  1: { bg: 'bg-teal-500/10', border: 'border-teal-500/30', text: 'text-teal-400', line: 'bg-teal-500/40' },
  2: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', line: 'bg-blue-500/40' },
  3: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', line: 'bg-amber-500/40' },
  4: { bg: 'bg-rose-500/10', border: 'border-rose-500/30', text: 'text-rose-400', line: 'bg-rose-500/40' },
};

const DEPTH_LABELS: Record<number, string> = {
  1: 'Direct',
  2: 'Level 2',
  3: 'Level 3',
  4: 'Level 4',
};

export const ReferralTree: React.FC<ReferralTreeProps> = ({ nodes, currentUserName }) => {
  const grouped = nodes.reduce<Record<number, ReferralTreeNode[]>>((acc, node) => {
    if (!acc[node.depth]) acc[node.depth] = [];
    acc[node.depth].push(node);
    return acc;
  }, {});

  const depths = Object.keys(grouped)
    .map(Number)
    .sort((a, b) => a - b);

  if (nodes.length === 0) {
    return (
      <div className="text-center py-12">
        <User size={40} className="mx-auto mb-4 text-white/20" />
        <p className="text-white/50 text-sm">No referrals yet. Share your code to start building your tree!</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/[0.06]">
        <div className="w-10 h-10 rounded-full bg-teal-500/20 border border-teal-500/40 flex items-center justify-center">
          <User size={18} className="text-teal-400" />
        </div>
        <div>
          <p className="font-semibold text-sm">{currentUserName || 'You'}</p>
          <p className="text-xs text-white/40">Root of your referral tree</p>
        </div>
      </div>

      <div className="space-y-6">
        {depths.map((depth) => {
          const color = DEPTH_COLORS[depth] || DEPTH_COLORS[4];
          const label = DEPTH_LABELS[depth] || `Level ${depth}`;
          const levelNodes = grouped[depth];

          return (
            <div key={depth}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`h-px flex-1 ${color.line}`} />
                <span className={`text-xs font-medium uppercase tracking-wider ${color.text}`}>
                  {label} ({levelNodes.length})
                </span>
                <div className={`h-px flex-1 ${color.line}`} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {levelNodes.map((node) => (
                  <div
                    key={node.id}
                    className={`${color.bg} border ${color.border} rounded-xl p-3 transition-all hover:scale-[1.02]`}
                  >
                    <div className="flex items-center gap-3">
                      {node.referred_avatar ? (
                        <img
                          src={node.referred_avatar}
                          alt=""
                          className="w-9 h-9 rounded-full object-cover border border-white/10"
                        />
                      ) : (
                        <div className={`w-9 h-9 rounded-full ${color.bg} border ${color.border} flex items-center justify-center`}>
                          <User size={14} className={color.text} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {node.referred_name || node.referred_username || 'Unknown'}
                        </p>
                        {node.referred_username && node.referred_name && (
                          <p className="text-xs text-white/40 truncate">@{node.referred_username}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-white/[0.06]">
                      <div className="flex items-center gap-1">
                        <CheckCircle size={12} className="text-green-400" />
                        <span className="text-xs text-white/50">Joined</span>
                      </div>
                      {node.has_subscription && (
                        <div className="flex items-center gap-1 ml-auto">
                          <CreditCard size={12} className="text-amber-400" />
                          <span className="text-xs text-amber-400 font-medium">Subscriber</span>
                        </div>
                      )}
                      {!node.has_subscription && (
                        <div className="flex items-center gap-1 ml-auto">
                          <Clock size={12} className="text-white/30" />
                          <span className="text-xs text-white/30">Free</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
